import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { API_BASE, SERVICE_STATUS, SERVICE_TYPES, normalizeServiceType } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, SecondaryButton, StatusBadge, inputCls } from "@/components/ui-kit";
import ServiceForm from "@/components/ServiceForm";
import ServiceTypeBadge from "@/components/ServiceTypeBadge";
import useTechnicians from "@/hooks/useTechnicians";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, MapPin, Download, X, CalendarClock, Filter, MailCheck } from "lucide-react";

const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const TEMPLATE_LABEL = {
  report: "Informe",
  report_and_quote: "Informe + Cotización",
  quote: "Cotización",
  reminder: "Recordatorio",
  custom: "Personalizado",
};

function fmtSentAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function toISO(d) { return d.toISOString().slice(0, 10); }
function buildMonthGrid(date) {
  const first = startOfMonth(date);
  const start = new Date(first);
  const dow = (first.getDay() + 6) % 7;
  start.setDate(first.getDate() - dow);
  return Array.from({ length: 42 }, (_, i) => {
    const c = new Date(start);
    c.setDate(start.getDate() + i);
    return c;
  });
}

function RescheduleModal({ service, onClose, onSaved }) {
  const [date, setDate] = useState(service.scheduled_date);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!date || date === service.scheduled_date) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch(`/services/${service.id}`, { scheduled_date: date });
      toast.success(`Servicio movido al ${date}`);
      onSaved();
    } catch {
      toast.error("No se pudo reagendar");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md border-t-4 border-[#FF5A00]" data-testid="reschedule-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Reagendar</div>
            <div className="font-display font-bold text-base">{service.customer_name}</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-neutral-500">Fecha actual: <b className="font-mono-kpi">{service.scheduled_date}</b></div>
          <label className="block">
            <span className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">Nueva fecha</span>
            <input type="date" required autoFocus data-testid="reschedule-date-input" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} mt-1`} />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="reschedule-save">{saving ? "Guardando…" : "Reagendar"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export default function CalendarPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(params.get("new") === "1");
  const [selectedDay, setSelectedDay] = useState(toISO(new Date()));
  const [reschedule, setReschedule] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ technician: "", type: "", status: "", customer: "", commune: "" });
  const { items: technicians } = useTechnicians();

  const load = async () => {
    const grid = buildMonthGrid(cursor);
    const start = toISO(grid[0]);
    const end = toISO(grid[grid.length - 1]);
    const { data } = await api.get("/services", { params: { start, end } });
    setServices(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  // Lista de comunas presentes en los servicios cargados (para el select).
  const communes = useMemo(() => {
    const set = new Set();
    services.forEach((s) => s.customer_commune && set.add(s.customer_commune));
    return [...set].sort();
  }, [services]);

  // Servicios filtrados
  const filtered = useMemo(() => {
    const q = filters.customer.trim().toLowerCase();
    return services.filter((s) => {
      if (filters.technician && s.technician !== filters.technician) return false;
      if (filters.type && normalizeServiceType(s.service_type) !== filters.type) return false;
      if (filters.status && s.status !== filters.status) return false;
      if (filters.commune && s.customer_commune !== filters.commune) return false;
      if (q && !(s.customer_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [services, filters]);

  const byDay = useMemo(() => {
    const map = {};
    filtered.forEach((s) => { const day = s.scheduled_date?.slice(0, 10); (map[day] = map[day] || []).push(s); });
    return map;
  }, [filtered]);

  const cells = buildMonthGrid(cursor);
  const monthLabel = cursor.toLocaleDateString("es-CL", { month: "long", year: "numeric" });
  const today = toISO(new Date());
  const selectedServices = byDay[selectedDay] || [];
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const clearFilters = () => setFilters({ technician: "", type: "", status: "", customer: "", commune: "" });

  const submit = async (payload) => {
    await api.post("/services", payload);
    toast.success("Servicio programado");
    load();
  };

  const exportPDF = async () => {
    try {
      const y = cursor.getFullYear();
      const m = cursor.getMonth() + 1;
      const res = await fetch(`${API_BASE}/calendar/export.pdf?year=${y}&month=${m}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Agenda_${y}-${String(m).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Agenda exportada");
    } catch {
      toast.error("Error generando PDF");
    }
  };

  const handleDrop = async (e, targetIso) => {
    e.preventDefault();
    const sid = e.dataTransfer.getData("text/service-id");
    const fromIso = e.dataTransfer.getData("text/service-from");
    if (!sid || !targetIso || fromIso === targetIso) return;
    try {
      await api.patch(`/services/${sid}`, { scheduled_date: targetIso });
      toast.success("Servicio movido");
      load();
    } catch {
      toast.error("No se pudo mover");
    }
  };
  const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  return (
    <PageContainer>
      <PageHeader
        kicker="Operación · Ruta diaria"
        title="Calendario de Mantenciones"
        subtitle="Visualiza, agenda y administra todos los servicios programados."
        actions={
          <div className="flex gap-2">
            <SecondaryButton data-testid="calendar-toggle-filters" onClick={() => setShowFilters((v) => !v)}>
              <Filter className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Filtros{activeFilterCount > 0 && ` (${activeFilterCount})`}
            </SecondaryButton>
            <SecondaryButton data-testid="calendar-export-pdf" onClick={exportPDF}>
              <Download className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Exportar PDF
            </SecondaryButton>
            <PrimaryButton data-testid="calendar-new-service" onClick={() => setShowForm(true)}>
              <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Nuevo servicio
            </PrimaryButton>
          </div>
        }
      />

      {(showFilters || activeFilterCount > 0) && (
        <div className="mb-4 bg-white border border-neutral-200 p-3" data-testid="calendar-filters">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <select data-testid="calendar-filter-technician" value={filters.technician} onChange={(e) => setFilters({ ...filters, technician: e.target.value })} className={inputCls}>
              <option value="">Todos los técnicos</option>
              {technicians.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            <select data-testid="calendar-filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className={inputCls}>
              <option value="">Todos los tipos</option>
              {SERVICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select data-testid="calendar-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls}>
              <option value="">Todos los estados</option>
              {SERVICE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select data-testid="calendar-filter-commune" value={filters.commune} onChange={(e) => setFilters({ ...filters, commune: e.target.value })} className={inputCls}>
              <option value="">Todas las comunas</option>
              {communes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              placeholder="Buscar cliente…"
              data-testid="calendar-filter-customer"
              value={filters.customer}
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              className={inputCls}
            />
          </div>
          {activeFilterCount > 0 && (
            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs text-neutral-500 font-mono-kpi">
                {filtered.length} de {services.length} servicios coinciden
              </div>
              <button data-testid="calendar-filters-clear" onClick={clearFilters} className="font-mono-kpi text-[10px] uppercase tracking-widest text-[#FF5A00] hover:text-[#E04A00] inline-flex items-center gap-1">
                <X className="w-3 h-3" />Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
        {/* Calendar grid */}
        <div className="bg-white border border-neutral-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
            <div className="flex items-center gap-2">
              <button data-testid="calendar-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="p-2 hover:bg-neutral-100"><ChevronLeft className="w-4 h-4" /></button>
              <button data-testid="calendar-next" onClick={() => setCursor(addMonths(cursor, 1))} className="p-2 hover:bg-neutral-100"><ChevronRight className="w-4 h-4" /></button>
              <SecondaryButton data-testid="calendar-today" onClick={() => { setCursor(startOfMonth(new Date())); setSelectedDay(today); }}>Hoy</SecondaryButton>
            </div>
            <div className="font-display font-bold text-lg capitalize">{monthLabel}</div>
          </div>

          <div className="grid grid-cols-7 border-b border-neutral-200">
            {DOW.map((d) => (
              <div key={d} className="px-3 py-2 font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 text-center border-r border-neutral-200 last:border-r-0">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {cells.map((c, i) => {
              const iso = toISO(c);
              const isOther = c.getMonth() !== cursor.getMonth();
              const isToday = iso === today;
              const isSelected = iso === selectedDay;
              const dayServices = byDay[iso] || [];
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(iso)}
                  onDragOver={allowDrop}
                  onDrop={(e) => handleDrop(e, iso)}
                  data-testid={`calendar-day-${iso}`}
                  className={`min-h-[100px] text-left border-r border-b border-neutral-200 last:border-r-0 p-2 transition-colors ${
                    isOther ? "bg-neutral-50/50 text-neutral-400" : "bg-white"
                  } ${isSelected ? "ring-2 ring-inset ring-[#FF5A00] z-10" : "hover:bg-neutral-50"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`font-mono-kpi text-sm font-bold ${isToday ? "bg-[#FF5A00] text-white px-1.5 py-0.5" : ""}`}>
                      {c.getDate()}
                    </div>
                    {dayServices.length > 0 && (
                      <div className="font-mono-kpi text-[10px] text-neutral-500">{dayServices.length}</div>
                    )}
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayServices.slice(0, 2).map((s) => (
                      <div
                        key={s.id}
                        draggable
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDay(iso);
                          navigate(`/admin/servicios/${s.id}`);
                        }}
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/service-id", s.id);
                          e.dataTransfer.setData("text/service-from", iso);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        data-testid={`calendar-chip-${s.id}`}
                        className="text-[10px] truncate border-l-2 pl-1 cursor-pointer hover:bg-neutral-100 active:cursor-grabbing"
                        style={{ borderLeftColor: (SERVICE_TYPES.find((t) => t.value === normalizeServiceType(s.service_type))?.color) || "#FF5A00", background: `${(SERVICE_TYPES.find((t) => t.value === normalizeServiceType(s.service_type))?.color) || "#FF5A00"}0d` }}
                        title="Click abre el servicio · Arrastra para mover de día"
                      >
                        <div className="flex items-center gap-1">
                          {s.last_email && (
                            <MailCheck className="w-2.5 h-2.5 text-[#008C3A] flex-shrink-0" data-testid={`calendar-chip-mailsent-${s.id}`} />
                          )}
                          <span className="truncate">{s.customer_name}</span>
                        </div>
                      </div>
                    ))}
                    {dayServices.length > 2 && (
                      <div className="text-[10px] text-neutral-500">+{dayServices.length - 2} más</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Day detail */}
        <aside className="bg-white border border-neutral-200">
          <div className="px-5 py-4 border-b border-neutral-200">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Día seleccionado</div>
            <div className="font-display font-bold text-xl mt-1">
              {new Date(selectedDay + "T12:00").toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div className="text-xs text-neutral-500 mt-1">{selectedServices.length} servicio(s) programado(s)</div>
          </div>
          <div className="p-4 space-y-3">
            {selectedServices.length === 0 && (
              <div className="text-sm text-neutral-500 text-center py-8 border border-dashed border-neutral-300">
                Sin servicios este día
              </div>
            )}
            {selectedServices.map((s) => (
              <div
                key={s.id}
                data-testid={`calendar-service-${s.id}`}
                className="border border-neutral-200 hover:border-[#0A0A0A] p-3 transition-colors"
              >
                <Link to={`/admin/servicios/${s.id}`} className="block">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-bold text-sm truncate">{s.customer_name}</div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <ServiceTypeBadge value={s.service_type} testid={`calendar-service-type-${s.id}`} />
                    {s.technician && (
                      <span className="text-xs text-neutral-500">· {s.technician}</span>
                    )}
                  </div>
                  <div className="flex items-start gap-1.5 text-xs text-neutral-500 mt-1.5">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="truncate">{s.customer_address}</span>
                  </div>
                </Link>
                {s.office_state && <Link data-testid={`calendar-review-link-${s.id}`} className="mt-2 block text-xs text-[#b44006] underline" to={`/admin/revision-visitas/${s.id}`}>{({ new: "Por revisar", pending_send: "Informe pendiente de envío", sent: "Informe enviado" })[s.office_state]} · Registro e informes</Link>}
                {s.last_email && (
                  <div
                    data-testid={`calendar-service-mail-log-${s.id}`}
                    className="mt-2 border border-[#00C853]/30 bg-[#00C853]/5 px-2 py-1.5 text-[11px] text-[#005E1E]"
                  >
                    <div className="flex items-center gap-1.5 font-mono-kpi text-[10px] uppercase tracking-widest text-[#008C3A]">
                      <MailCheck className="w-3 h-3" />Correo enviado
                    </div>
                    <div className="mt-0.5 leading-snug">
                      {TEMPLATE_LABEL[s.last_email.template] || s.last_email.template} → <b>{s.last_email.to}</b>
                    </div>
                    <div className="text-neutral-500 leading-snug">
                      Por <b>{s.last_email.sent_by || "—"}</b> · {fmtSentAt(s.last_email.sent_at)}
                    </div>
                  </div>
                )}
                {s.reschedule_request?.status === "pending" && ["scheduled", "in_progress"].includes(s.status) && <Link data-testid={`calendar-date-request-${s.id}`} className="block mt-2 text-xs text-amber-800 underline" to={`/admin/revision-visitas/${s.id}`}>Reprogramación solicitada</Link>}
                <button
                  onClick={() => setReschedule(s)}
                  data-testid={`calendar-reschedule-${s.id}`}
                  className="mt-2 w-full font-mono-kpi text-[10px] uppercase tracking-widest text-[#FF5A00] hover:bg-[#FF5A00]/5 border border-[#FF5A00]/30 px-2 py-1.5 inline-flex items-center justify-center gap-1.5 transition-colors"
                >
                  <CalendarClock className="w-3 h-3" />Reagendar
                </button>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {showForm && (
        <ServiceForm
          initial={{ scheduled_date: selectedDay }}
          onClose={() => setShowForm(false)}
          onSubmit={submit}
        />
      )}
      {reschedule && (
        <RescheduleModal
          service={reschedule}
          onClose={() => setReschedule(null)}
          onSaved={() => { setReschedule(null); load(); }}
        />
      )}
    </PageContainer>
  );
}
